import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { scalarCookieConsentConfig } from './scalarCookieConsentConfig';

declare global {
  interface Window {
    CookieConsent?: {
      run?: (config: typeof scalarCookieConsentConfig) => void;
      showPreferences?: () => void;
      // Add other methods if needed
    };
  }
}

interface CookieConsentContextType {
  consent: {
    analytics: boolean;
    marketing: boolean;
    functionality: boolean;
    necessary: boolean;
  };
  showPreferences: () => void;
}

// 0. Load external cookie consent CSS if not already present
if (
  !document.querySelector(
    'link[href="https://scalar.io/wp-content/plugins/beautiful-and-responsive-cookie-consent-addon/public/css/cookieconsent.fb.nsc.css"]'
  )
) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href =
    'https://scalar.io/wp-content/plugins/beautiful-and-responsive-cookie-consent-addon/public/css/cookieconsent.fb.nsc.css';
  link.id = 'scalar-cookieconsent-css';
  document.head.appendChild(link);
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const COOKIE_CONSENT_SRC =
  'https://scalar.io/wp-content/plugins/beautiful-and-responsive-cookie-consent-addon/public/js/cookieconsent.nsc.umd.js?cb=4.0.2';
const COOKIE_CONSENT_CSS =
  'https://scalar.io/wp-content/plugins/beautiful-and-responsive-cookie-consent/public/cookieNSCconsent.min.css';
const COOKIE_CONSENT_CSS_2 =
  'https://scalar.io/wp-content/plugins/beautiful-and-responsive-cookie-consent-addon/public/css/cookieconsent.fb.nsc.css';

const CONSENT_COOKIE_NAME = 'scalar-consent';

function getConsentFromCookie(): CookieConsentContextType['consent'] {
  const regex = new RegExp(`(^|;)\\s*${CONSENT_COOKIE_NAME}\\s*=\\s*([^;]+)`);
  const match = regex.exec(document.cookie);

  if (!match) {
    return {
      analytics: false,
      marketing: false,
      functionality: false,
      necessary: true,
    };
  }

  const decoded = decodeURIComponent(match[2] || '');
  let parsed: { categories?: string[] } = {};

  try {
    parsed = JSON.parse(decoded);
  } catch {
    // fallback if JSON parsing fails
    return {
      analytics: false,
      marketing: false,
      functionality: false,
      necessary: true,
    };
  }

  const cats = parsed.categories || [];

  return {
    analytics: cats.includes('Analytics'),
    marketing: cats.includes('Marketing'),
    functionality: cats.includes('Functionality'),
    necessary: cats.includes('Necessary'),
  };
}

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState(getConsentFromCookie());

  // Expose showPreferences
  const showPreferences = useCallback(() => {
    if (window.CookieConsent && typeof window.CookieConsent.showPreferences === 'function') {
      window.CookieConsent.showPreferences();
    }
  }, []);

  // Context value
  const cookieConsentContextValue = useMemo<CookieConsentContextType>(
    () => ({
      consent,
      showPreferences,
    }),
    [consent, showPreferences]
  );

  // Listen for consent changes
  useEffect(() => {
    function updateConsent() {
      setConsent(getConsentFromCookie());
    }
    window.addEventListener('cookieconsent_update', updateConsent);
    window.addEventListener('cookieconsent_accept', updateConsent);
    window.addEventListener('cookieconsent_reject', updateConsent);
    // Also poll on focus (in case changed in another tab)
    window.addEventListener('focus', updateConsent);
    return () => {
      window.removeEventListener('cookieconsent_update', updateConsent);
      window.removeEventListener('cookieconsent_accept', updateConsent);
      window.removeEventListener('cookieconsent_reject', updateConsent);
      window.removeEventListener('focus', updateConsent);
    };
  }, []);

  // Load CSS, JS, and config
  useEffect(() => {
    // CSS
    if (!document.querySelector(`link[href="${COOKIE_CONSENT_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = COOKIE_CONSENT_CSS;
      link.id = 'scalar-cookieconsent-css';
      document.head.appendChild(link);
    }
    if (!document.querySelector(`link[href="${COOKIE_CONSENT_CSS_2}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = COOKIE_CONSENT_CSS_2;
      link.id = 'scalar-cookieconsent-css';
      document.head.appendChild(link);
    }
    // Main JS
    function loadConsentJs() {
      if (!document.querySelector(`script[src="${COOKIE_CONSENT_SRC}"]`)) {
        const script = document.createElement('script');
        script.src = COOKIE_CONSENT_SRC;
        script.async = true;
        script.onload = function () {
          if (window.CookieConsent && typeof window.CookieConsent.run === 'function') {
            window.CookieConsent.run(scalarCookieConsentConfig);
          }
        };
        document.body.appendChild(script);
      } else if (window.CookieConsent && typeof window.CookieConsent.run === 'function') {
        window.CookieConsent.run(scalarCookieConsentConfig);
      }
    }
    loadConsentJs();
    // Inline scripts (if needed) can be added here
  }, []);

  // Inject .cm-wrapper { display: none; } CSS on mount
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'cm-wrapper-hide-style';
    style.textContent = '.cm-wrapper { display: none !important; }';
    document.head.appendChild(style);
    return () => {
      const existing = document.getElementById('cm-wrapper-hide-style');
      if (existing) {
        existing.remove();
      }
    };
  }, []);

  return <CookieConsentContext.Provider value={cookieConsentContextValue}>{children}</CookieConsentContext.Provider>;
};

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);

  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }

  return ctx;
}
